/**
 * Live-edit round-trip for the fixed-DOCX review page.
 *
 * The review page renders the merged .docx as HTML (docx-preview) and lets the user edit a
 * block's text in a sidebar. To persist that edit we must write it back into the .docx —
 * NOT the rendered HTML, and NOT the PDF (which is only produced at download/send).
 *
 * This reuses the tokenizer's run-aware text model: flatten every <w:t> into one string,
 * locate the edited block by its ORIGINAL text + surrounding context (run boundaries are
 * irrelevant once flattened), then substitute the new text into the FIRST covering run and
 * blank the rest — exactly how the tokenizer injects a `{token}`, but the replacement is the
 * edited text instead of a token.
 *
 * Limitation (shared with the tokenizer): a block whose original text spans multiple runs
 * with mixed formatting collapses to the first run's <w:rPr>. Acceptable for contract body
 * text; structural edits (adding/removing paragraphs) are out of scope.
 */

import {
  applyNodeEdits,
  buildTextModel,
  loadDocxDocument,
  locate,
  serializeDocxDocument,
  spanToEdits,
} from './tokenize';

export interface TextEditInput {
  /** The block's text as it currently appears in the document (what the user started from). */
  originalText: string;
  /** The edited text to substitute in. */
  newText: string;
  /** A few chars immediately before the block (disambiguates repeated text). */
  contextBefore?: string;
  /** A few chars immediately after the block. */
  contextAfter?: string;
  /** Coarse paragraph anchor from the renderer (stored on the model; not load-bearing). */
  paragraphIndex?: number | null;
}

export class TextEditError extends Error {}

/**
 * Apply a single block-text substitution to a .docx and return the new bytes.
 * Throws {@link TextEditError} with a human-readable reason when the block can't be located
 * unambiguously (the document is left unmodified).
 */
export function applyTextEdit(input: Uint8Array | ArrayBuffer, edit: TextEditInput): Uint8Array {
  const { zip, doc } = loadDocxDocument(input);
  const model = buildTextModel(doc);

  const located = locate(model, {
    name: 'edit',
    selectedText: edit.originalText,
    contextBefore: edit.contextBefore,
    contextAfter: edit.contextAfter,
    paragraphIndex: edit.paragraphIndex ?? null,
  });
  if ('error' in located) {
    throw new TextEditError(located.error);
  }

  const edits = spanToEdits(model, located.rawStart, located.rawEnd, edit.newText);
  if ('error' in edits) {
    throw new TextEditError(edits.error);
  }

  applyNodeEdits(model, doc, edits);
  return serializeDocxDocument(zip, doc);
}
